import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import {
  TYPE_SCALE,
  PADDING_X,
  PADDING_Y,
  POSITION_SCALE,
  MARGIN_Y,
  RADIUS_SCALE,
  COLOR_SCALE,
  TRANSFORM_SCALE,
  SIZE_SCALE,
  SHADOW_SCALE,
  Z_INDEX,
  SURFACE_PADDING,
  TRANSITION,
} from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { cn } from '@/lib/utils';

const alertVariants = cva(
  `relative w-full ${RADIUS_SCALE.lg} border ${PADDING_X['4']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} [&>svg+div]:${TRANSFORM_SCALE['translate-y']['-3px']} [&>svg]:absolute ${POSITION_SCALE.left['4']} ${POSITION_SCALE.top['4']} [&>svg]:${COLOR_SCALE.foreground} [&>svg~*]:${PADDING_X['6']} animate-in slide-in-from-top-2 duration-300`,
  {
    variants: {
      variant: {
        default: `bg-background text-foreground border-border`,
        destructive: `border-destructive/50 text-destructive bg-destructive/5 [&>svg]:text-destructive`,
        success: `border-green-200 text-green-800 bg-green-50 [&>svg]:text-green-600`,
        warning: `border-yellow-200 text-yellow-800 bg-yellow-50 [&>svg]:text-yellow-600`,
        info: `border-blue-200 text-blue-800 bg-blue-50 [&>svg]:text-blue-600`,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type AlertProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>;

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
  )
);
Alert.displayName = 'Alert';

type AlertTitleProps = React.HTMLAttributes<HTMLHeadingElement>;

const AlertTitle = React.forwardRef<HTMLHeadingElement, AlertTitleProps>(
  ({ className, children, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn(`${MARGIN_Y['1']} font-medium leading-none tracking-tight`, className)}
      {...props}
    >
      {children}
    </h5>
  )
);
AlertTitle.displayName = 'AlertTitle';

type AlertDescriptionProps = React.HTMLAttributes<HTMLDivElement>;

const AlertDescription = React.forwardRef<HTMLDivElement, AlertDescriptionProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(`${TYPE_SCALE.sm} [&_p]:leading-relaxed`, className)} {...props} />
  )
);
AlertDescription.displayName = 'AlertDescription';

// Custom hook for alert visibility management
const useAlertVisibility = (autoHide: boolean, autoHideDelay: number, onDismiss?: () => void) => {
  const [isVisible, setIsVisible] = React.useState(true);
  const [isHiding, setIsHiding] = React.useState(false);

  const handleDismiss = React.useCallback(() => {
    setIsHiding(true);
    setTimeout(() => {
      setIsVisible(false);
      onDismiss?.();
    }, 300); // Match animation duration
  }, [onDismiss]);

  React.useEffect(() => {
    if (autoHide && isVisible) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoHide, autoHideDelay, isVisible, handleDismiss]);

  return { isVisible, isHiding, handleDismiss };
};

// Helper function to render alert actions
const renderAlertActions = (
  actions: React.ReactNode,
  dismissible: boolean,
  handleDismiss: () => void
) => {
  if (!actions && !dismissible) return null;

  return (
    <div className="flex items-center gap-2 ml-auto">
      {actions}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className={`${SURFACE_PADDING['1']} ${RADIUS_SCALE.md} hover:bg-black/10 ${TRANSITION.colors}`}
          aria-label="Dismiss alert"
        >
          <span className={`text-current ${TYPE_SCALE.sm}`}>✕</span>
        </button>
      )}
    </div>
  );
};

// Enhanced Alert with auto-dismiss and action buttons
interface EnhancedAlertProps extends AlertProps {
  autoHide?: boolean;
  autoHideDelay?: number;
  dismissible?: boolean;
  onDismiss?: () => void;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
}

const EnhancedAlert = React.forwardRef<HTMLDivElement, EnhancedAlertProps>(
  (
    {
      className,
      variant,
      autoHide = false,
      autoHideDelay = 5000,
      dismissible = false,
      onDismiss,
      actions,
      icon,
      children,
      ...props
    },
    ref
  ) => {
    const { isVisible, isHiding, handleDismiss } = useAlertVisibility(
      autoHide,
      autoHideDelay,
      onDismiss
    );

    if (!isVisible) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          alertVariants({ variant }),
          isHiding && 'animate-out slide-out-to-top-2 duration-300',
          className
        )}
        role="alert"
        {...props}
      >
        {icon && (
          <div
            className={`${POSITION_SCALE.absolute} ${POSITION_SCALE.left['4']} ${POSITION_SCALE.top['4']}`}
          >
            {icon}
          </div>
        )}
        <div className={cn(icon && 'pl-6')}>{children}</div>
        {renderAlertActions(actions, dismissible, handleDismiss)}
      </div>
    );
  }
);
EnhancedAlert.displayName = 'EnhancedAlert';

// Toast-style alert that appears briefly
interface ToastAlertProps extends Omit<EnhancedAlertProps, 'autoHide'> {
  duration?: number;
}

const ToastAlert = React.forwardRef<HTMLDivElement, ToastAlertProps>(
  ({ duration = 3000, ...props }, ref) => (
    <EnhancedAlert
      ref={ref}
      autoHide
      autoHideDelay={duration}
      dismissible
      className={`${POSITION_SCALE.fixed} ${POSITION_SCALE.top['4']} ${POSITION_SCALE.right['4']} ${Z_INDEX.modal} ${SIZE_SCALE.maxWsm} ${SHADOW_SCALE.lg}`}
      {...props}
    />
  )
);
ToastAlert.displayName = 'ToastAlert';

// Wrap EnhancedAlert with memory leak monitor
const EnhancedAlertWithMonitor = withMemoryLeakMonitor(EnhancedAlert, {
  componentName: 'EnhancedAlert',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  renderThreshold: 10,
  frequencyThreshold: 2,
});

export {
  Alert,
  AlertTitle,
  AlertDescription,
  EnhancedAlertWithMonitor as EnhancedAlert,
  ToastAlert,
};
